import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

function QuoteSection() {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  const [quoteStatus, setQuoteStatus] = useState({
    type: "",
    message: "",
  });

  const [quoteForm, setQuoteForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  });

  const [isQuoteConsentChecked, setIsQuoteConsentChecked] = useState(false);
  const [quoteContractModalOpen, setQuoteContractModalOpen] = useState(false);
  const [quoteContractLoading, setQuoteContractLoading] = useState(false);
  const [quoteContractError, setQuoteContractError] = useState("");
  const [quoteContractData, setQuoteContractData] = useState({ title: "", editor: "" });

  /*
  |--------------------------------------------------------------------------
  | Modal açıkken body scroll kapat
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!isQuoteModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsQuoteModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleEscape);
    };
  }, [isQuoteModalOpen]);

  /*
  |--------------------------------------------------------------------------
  | Modal aç / kapat
  |--------------------------------------------------------------------------
  */
  const openQuoteModal = () => {
    setQuoteStatus({
      type: "",
      message: "",
    });

    setIsQuoteModalOpen(true);
  };

  const closeQuoteModal = () => {
    if (quoteSubmitting) {
      return;
    }

    setIsQuoteModalOpen(false);

    setQuoteStatus({
      type: "",
      message: "",
    });
  };

  const openQuoteContractModal = async () => {
    setQuoteContractModalOpen(true);
    setQuoteContractLoading(true);
    setQuoteContractError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/contract/394`);

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || data?.msg || "Aydınlatma metni alınamadı.");
      }

      setQuoteContractData({
        title: data?.title || "Aydınlatma Metni",
        editor: data?.editor || "",
      });
    } catch (error) {
      console.error("CONTRACT_DETAIL_ERROR:", error);
      setQuoteContractError("Aydınlatma metni yüklenirken bir hata oluştu.");
    } finally {
      setQuoteContractLoading(false);
    }
  };

  const closeQuoteContractModal = () => {
    setQuoteContractModalOpen(false);
  };

  useEffect(() => {
    if (!quoteContractModalOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeQuoteContractModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [quoteContractModalOpen]);

  /*
  |--------------------------------------------------------------------------
  | Telefon formatı
  |--------------------------------------------------------------------------
  */
  const formatQuotePhone = (value) => {
    let digits = String(value || "").replace(/\D/g, "");

    // Kullanıcı 90 ile yapıştırırsa kaldır
    if (digits.startsWith("90")) {
      digits = digits.slice(2);
    }

    // Kullanıcı 0 ile başlarsa kaldır
    if (digits.startsWith("0")) {
      digits = digits.slice(1);
    }

    digits = digits.slice(0, 10);

    const firstPart = digits.slice(0, 3);
    const secondPart = digits.slice(3, 6);
    const thirdPart = digits.slice(6, 8);
    const fourthPart = digits.slice(8, 10);

    return [firstPart, secondPart, thirdPart, fourthPart]
      .filter(Boolean)
      .join(" ");
  };

  /*
  |--------------------------------------------------------------------------
  | Form değişiklikleri
  |--------------------------------------------------------------------------
  */
  const handleQuoteChange = (event) => {
    const { name, value } = event.target;

    let nextValue = value;

    /*
     * Ad soyad rakam kabul etmesin
     */
    if (name === "fullName") {
      nextValue = value.replace(/[0-9]/g, "");
    }

    setQuoteForm((current) => ({
      ...current,
      [name]: nextValue,
    }));

    if (quoteStatus.type === "error") {
      setQuoteStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleQuotePhoneChange = (value) => {
    setQuoteForm((current) => ({
      ...current,
      phone: value ? `+${value}` : "",
    }));

    if (quoteStatus.type === "error") {
      setQuoteStatus({
        type: "",
        message: "",
      });
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Form gönder
  |--------------------------------------------------------------------------
  */
  const handleQuoteSubmit = async (event) => {
    event.preventDefault();

    if (quoteSubmitting) {
      return;
    }

    const formElement = event.currentTarget;

    setQuoteStatus({
      type: "",
      message: "",
    });

    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    const cleanFullName = quoteForm.fullName.trim();

    const cleanPhone = quoteForm.phone.trim();

    const cleanEmail = quoteForm.email.trim();

    const cleanMessage = quoteForm.message.trim();

    if (!cleanFullName || !cleanPhone || !cleanEmail || !cleanMessage) {
      setQuoteStatus({
        type: "error",
        message: "Lütfen bütün zorunlu alanları doldurun.",
      });

      return;
    }

    if (!isQuoteConsentChecked) {
      setQuoteStatus({
        type: "error",
        message: "Lütfen kişisel verilerin işlenmesine ilişkin aydınlatma metnini okuyup onaylayınız.",
      });

      return;
    }

    try {
      setQuoteSubmitting(true);

      const searchParams = new URLSearchParams(window.location.search);

      const currentLang = searchParams.get("lang") || "tr";

      const response = await fetch(
        `${API_BASE_URL}/api/contact/send-quote-request`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify({
            fullName: cleanFullName,
            phone: cleanPhone,
            email: cleanEmail,
            message: cleanMessage,
            lang: currentLang,
          }),
        },
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok || responseData.success === false) {
        throw new Error(responseData.message || "Teklif talebi gönderilemedi.");
      }

      setQuoteForm({
        fullName: "",
        phone: "",
        email: "",
        message: "",
      });

      setIsQuoteConsentChecked(false);

      setQuoteStatus({
        type: "success",
        message:
          responseData.message || "Teklif talebiniz başarıyla gönderildi.",
      });
    } catch (error) {
      console.error("QUOTE_REQUEST_ERROR:", error);

      setQuoteStatus({
        type: "error",
        message:
          error.message || "Teklif talebi gönderilirken bir hata oluştu.",
      });
    } finally {
      setQuoteSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
          /* =============================================
             TEKLİF ŞERİDİ
          ============================================= */

          .demirsan-quote-section {
            position: relative;
            width: 100%;

            margin: 0 !important;
            padding: 34px 0 !important;

            overflow: hidden;

            background:
              linear-gradient(
                105deg,
                #c70512 0%,
                #e20614 48%,
                #b80410 100%
              );

            border-top:
              1px solid rgba(255,255,255,.12);

            box-shadow:
              0 -10px 30px
              rgba(11,23,53,.08);
          }

          .demirsan-quote-section::before {
            content: "";

            position: absolute;
            top: -210px;
            left: -150px;

            width: 420px;
            height: 420px;

            border:
              1px solid
              rgba(255,255,255,.08);

            border-radius: 50%;

            pointer-events: none;
          }

          .demirsan-quote-section::after {
            content: "";

            position: absolute;
            right: -180px;
            bottom: -310px;

            width: 520px;
            height: 520px;

            background:
              radial-gradient(
                circle,
                rgba(78,0,8,.20),
                transparent 68%
              );

            pointer-events: none;
          }

          .demirsan-quote-section .container {
            position: relative;
            z-index: 2;
          }

          .demirsan-quote-inner {
            display: flex;
            align-items: center;
            justify-content: space-between;

            gap: 40px;
          }

          .demirsan-quote-content {
            flex: 1;

            max-width: 830px;
            min-width: 0;
          }

          .demirsan-quote-label {
            display: block;

            margin-bottom: 5px;

            color:
              rgba(255,255,255,.76);

            font-size: 11px;
            font-weight: 700;
            letter-spacing: 1px;

            text-transform: uppercase;
          }

          .demirsan-quote-title {
            margin: 0 0 6px;

            color: #ffffff;

            font-size: 27px;
            font-weight: 700;
            line-height: 1.28;
          }

          .demirsan-quote-description {
            max-width: 700px;

            margin: 0;

            color:
              rgba(255,255,255,.80);

            font-size: 14px;
            line-height: 1.6;
          }

          .demirsan-quote-action {
            flex: 0 0 auto;
          }

          .demirsan-quote-button {
            display: inline-flex;

            align-items: center;
            justify-content: center;

            gap: 10px;

            min-width: 148px;
            min-height: 48px;

            padding: 11px 22px;

            color: #e20614;
            background: #ffffff;

            border: 2px solid #ffffff;
            border-radius: 7px;

            font-family: inherit;

            font-size: 13px;
            font-weight: 700;

            cursor: pointer;

            box-shadow:
              0 8px 20px
              rgba(71,0,7,.18);

            transition:
              color .2s ease,
              background-color .2s ease,
              transform .2s ease;
          }

          .demirsan-quote-button:hover {
            color: #ffffff;

            background: transparent;

            transform: translateY(-2px);
          }

          .demirsan-quote-button i {
            transition:
              transform .2s ease;
          }

          .demirsan-quote-button:hover i {
            transform:
              translateX(4px);
          }


          /* =============================================
             MODAL OVERLAY
          ============================================= */

          .demirsan-quote-modal-overlay {
            position: fixed;
            inset: 0;

            z-index: 1000000;

            display: flex;

            align-items: center;
            justify-content: center;

            padding: 24px;

            background:
              rgba(4,9,18,.74);

            backdrop-filter: blur(5px);

            opacity: 0;
            visibility: hidden;
            pointer-events: none;

            transition:
              opacity .22s ease,
              visibility .22s ease;
          }

          .demirsan-quote-modal-overlay.open {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
          }


          /* =============================================
             MODAL
          ============================================= */

          .demirsan-quote-modal {
            position: relative;

            width: min(760px, 100%);

            max-height:
              calc(100vh - 48px);

            padding: 42px;

            overflow-y: auto;
            overflow-x: hidden;

            background: #ffffff;

            border-radius: 16px;

            box-shadow:
              0 24px 70px
              rgba(0,0,0,.30);

            transform:
              translateY(18px)
              scale(.98);

            transition:
              transform .22s ease;
          }

          .demirsan-quote-modal-overlay.open
          .demirsan-quote-modal {
            transform:
              translateY(0)
              scale(1);
          }

          .demirsan-quote-modal-close {
            position: absolute;

            top: 18px;
            right: 18px;

            display: flex;

            align-items: center;
            justify-content: center;

            width: 34px;
            height: 34px;

            padding: 0;

            color: #ffffff;
            background: #e20614;

            border: none;
            border-radius: 50%;

            cursor: pointer;
          }

          .demirsan-quote-modal-label {
            display: block;

            margin-bottom: 6px;

            color: #e20614;

            font-size: 13px;
            font-weight: 700;
            letter-spacing: .6px;

            text-align: center;
            text-transform: uppercase;
          }

          .demirsan-quote-modal-title {
            margin: 0 0 8px;

            color: #0b1735;

            font-size: 34px;
            font-weight: 700;
            line-height: 1.25;

            text-align: center;
          }

          .demirsan-quote-modal-description {
            max-width: 560px;

            margin:
              0 auto 28px;

            color: #667085;

            font-size: 14px;
            line-height: 1.65;

            text-align: center;
          }


          /* =============================================
             FORM
          ============================================= */

          .demirsan-quote-form-grid {
            display: grid;

            grid-template-columns:
              repeat(2, minmax(0,1fr));

            gap: 16px;
          }

          .demirsan-quote-field {
            width: 100%;
          }

          .demirsan-quote-field.full-width {
            grid-column: 1 / -1;
          }

          .demirsan-quote-field label {
            display: block;

            margin-bottom: 7px;

            color: #17213a;

            font-size: 13px;
            font-weight: 600;
          }

          .demirsan-quote-field input,
          .demirsan-quote-field textarea {
            display: block;

            width: 100%;

            margin: 0;
            padding: 14px 16px;

            color: #17213a;

            background: #f6f7f9;

            border:
              1px solid transparent;

            border-radius: 9px;

            outline: none;

            font-family: inherit;

            font-size: 14px;
            line-height: 1.5;

            transition:
              border-color .2s ease,
              box-shadow .2s ease,
              background-color .2s ease;
          }

          .demirsan-quote-field input {
            height: 52px;
          }

          .demirsan-quote-field textarea {
            min-height: 135px;

            resize: vertical;
          }

          .demirsan-quote-field input:focus,
          .demirsan-quote-field textarea:focus {
            background: #ffffff;

            border-color:
              rgba(226,6,20,.5);

            box-shadow:
              0 0 0 4px
              rgba(226,6,20,.08);
          }

          .demirsan-quote-field input:disabled,
          .demirsan-quote-field textarea:disabled {
            cursor: not-allowed;

            opacity: .7;
          }


          /* =============================================
             FORM DURUMU
          ============================================= */

          .demirsan-quote-form-status {
            width: 100%;

            margin-top: 20px;
            padding: 13px 16px;

            border: 1px solid;
            border-radius: 8px;

            font-size: 14px;
            font-weight: 500;
            line-height: 1.55;

            text-align: center;
          }

          .demirsan-quote-form-status.success {
            color: #256337;

            background: #effaf2;

            border-color: #c8dfcf;
          }

          .demirsan-quote-form-status.error {
            color: #a20c18;

            background: #fff0f1;

            border-color: #f1c8cc;
          }


          /* =============================================
             SUBMIT
          ============================================= */

          .demirsan-quote-submit-area {
            display: flex;

            justify-content: center;

            margin-top: 22px;
          }

          .demirsan-quote-submit-button {
            display: inline-flex;

            align-items: center;
            justify-content: center;

            gap: 9px;

            min-width: 175px;
            min-height: 52px;

            padding: 13px 24px;

            color: #ffffff;
            background: #e20614;

            border: none;
            border-radius: 9px;

            font-family: inherit;

            font-size: 14px;
            font-weight: 700;

            cursor: pointer;
          }

          .demirsan-quote-submit-button:disabled {
            cursor: not-allowed;

            opacity: .65;
          }


          /* =============================================
             ONAY KUTUSU
          ============================================= */

          .demirsan-quote-consent {
            display: flex !important;
            align-items: flex-start !important;
            gap: 11px;
            grid-column: 1 / -1;
            margin: 0;
          }

          .demirsan-quote-consent-checkbox {
            position: static !important;
            display: block !important;
            flex: 0 0 18px !important;
            width: 18px !important;
            min-width: 18px !important;
            max-width: 18px !important;
            height: 18px !important;
            min-height: 18px !important;
            max-height: 18px !important;
            margin: 3px 0 0 !important;
            padding: 0 !important;
            opacity: 1 !important;
            visibility: visible !important;
            border: initial !important;
            border-radius: 3px;
            background: initial !important;
            box-shadow: none !important;
            transform: none !important;
            appearance: auto !important;
            -webkit-appearance: checkbox !important;
            accent-color: #e20614;
            cursor: pointer;
          }

          .demirsan-quote-consent-content {
            flex: 1;
            min-width: 0;
            margin: 0;
            padding: 0;
            color: #667085;
            font-size: 13px;
            font-weight: 400;
            line-height: 1.6;
          }

          .demirsan-quote-consent-content label {
            display: inline !important;
            margin: 0 !important;
            padding: 0 !important;
            color: #667085 !important;
            font-size: 13px !important;
            font-weight: 400 !important;
            line-height: 1.6 !important;
            cursor: pointer;
          }

          .demirsan-quote-contract-link {
            display: inline !important;
            width: auto !important;
            height: auto !important;
            margin: 0 3px !important;
            padding: 0 !important;
            border: 0 !important;
            border-radius: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
            color: #e20614 !important;
            font-family: inherit !important;
            font-size: inherit !important;
            font-weight: 600 !important;
            line-height: inherit !important;
            text-decoration: underline;
            text-underline-offset: 2px;
            vertical-align: baseline;
            cursor: pointer;
          }

          .demirsan-quote-contract-link:hover {
            color: #0b1735 !important;
            background: transparent !important;
          }


          /* =============================================
             SÖZLEŞME MODAL
          ============================================= */

          .demirsan-quote-contract-overlay {
            position: fixed;
            inset: 0;
            z-index: 1000010;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            background: rgba(7, 16, 36, 0.72);
            backdrop-filter: blur(4px);
          }

          .demirsan-quote-contract-modal {
            position: relative;
            display: flex;
            flex-direction: column;
            width: min(900px, 100%);
            max-height: calc(100vh - 48px);
            overflow: hidden;
            border-radius: 14px;
            background: #ffffff;
            box-shadow: 0 25px 80px rgba(0, 0, 0, 0.28);
          }

          .demirsan-quote-contract-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            padding: 16px 20px;
            border-bottom: 1px solid #eceef2;
            background: #ffffff;
          }

          .demirsan-quote-contract-label {
            display: block;
            margin-bottom: 4px;
            color: #e20614;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.6px;
            text-transform: uppercase;
          }

          .demirsan-quote-contract-title {
            margin: 0;
            color: #0b1735;
            font-size: 17px;
            font-weight: 700;
            line-height: 1.35;
          }

          .demirsan-quote-contract-close {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 38px;
            width: 38px;
            height: 38px;
            margin: 0;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #f4f5f7;
            color: #5d6677;
            font-size: 24px;
            line-height: 1;
            cursor: pointer;
            transition: background-color .2s ease, color .2s ease;
          }

          .demirsan-quote-contract-close:hover {
            background: #e20614;
            color: #ffffff;
          }

          .demirsan-quote-contract-body {
            flex: 1;
            padding: 26px;
            overflow-y: auto;
            color: #4f5b6e;
            font-size: 14px;
            line-height: 1.8;
          }

          .demirsan-quote-contract-content { color: #4f5b6e; font-size: 14px; line-height: 1.8; }
          .demirsan-quote-contract-content p { margin: 0 0 14px; }
          .demirsan-quote-contract-content h1,
          .demirsan-quote-contract-content h2,
          .demirsan-quote-contract-content h3,
          .demirsan-quote-contract-content h4,
          .demirsan-quote-contract-content h5,
          .demirsan-quote-contract-content h6 {
            margin-top: 16px !important;
            margin-bottom: 8px !important;
            color: #0b1735 !important;
            font-weight: 700 !important;
            line-height: 1.4 !important;
            letter-spacing: 0 !important;
            text-transform: none !important;
            background: none !important;
          }
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h1 { font-size: 20px !important; }
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h2 { font-size: 18px !important; }
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h3,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h4 { font-size: 16px !important; }
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h5,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h6 { font-size: 14px !important; }
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h1::before,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h1::after,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h2::before,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h2::after,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h3::before,
          body .demirsan-quote-contract-modal .demirsan-quote-contract-content h3::after {
            content: none !important;
            display: none !important;
            background: none !important;
          }
          .demirsan-quote-contract-content ul,
          .demirsan-quote-contract-content ol { padding-left: 22px; }
          .demirsan-quote-contract-content a { color: #e20614; }

          .demirsan-quote-contract-loading,
          .demirsan-quote-contract-error,
          .demirsan-quote-contract-empty { padding: 45px 15px; text-align: center; font-size: 14px; }
          .demirsan-quote-contract-loading { color: #657083; }
          .demirsan-quote-contract-error { color: #d40000; }
          .demirsan-quote-contract-empty { color: #7a8494; }

          .demirsan-quote-contract-footer {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px 25px;
            border-top: 1px solid #eceef2;
            background: #fafafa;
          }

          .demirsan-quote-contract-footer-button {
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
            width: auto !important;
            min-width: 90px !important;
            padding: 9px 18px !important;
            border: 0;
            border-radius: 6px;
            background: #0b1735;
            color: #ffffff;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
          }

          .demirsan-quote-contract-footer-button:hover { background: #e20614; }

          @media (max-width: 767px) {
            .demirsan-quote-contract-overlay { padding: 10px; }
            .demirsan-quote-contract-modal { max-height: calc(100vh - 20px); border-radius: 10px; }
            .demirsan-quote-contract-header { padding: 17px; }
            .demirsan-quote-contract-title { font-size: 18px; }
            .demirsan-quote-contract-body { padding: 18px; font-size: 13px; }
            .demirsan-quote-contract-footer { padding: 13px 17px; }
          }


          /* =============================================
             MOBILE
          ============================================= */

          @media (max-width: 767.98px) {
            .demirsan-quote-section {
              padding:
                28px 0 !important;
            }

            .demirsan-quote-inner {
              flex-direction: column;

              align-items: flex-start;

              gap: 18px;
            }

            .demirsan-quote-title {
              font-size: 22px;
            }

            .demirsan-quote-description {
              font-size: 13px;
            }

            .demirsan-quote-action,
            .demirsan-quote-button {
              width: 100%;
            }

            .demirsan-quote-modal-overlay {
              align-items: flex-end;

              padding: 12px;
            }
              .demirsan-phone-container .demirsan-phone-dropdown {
  width: min(330px, calc(100vw - 64px)) !important;
}

            .demirsan-quote-modal {
              width: 100%;

              max-height:
                calc(100vh - 24px);

              padding:
                38px 20px 24px;

              border-radius:
                16px 16px 10px 10px;
            }

            .demirsan-quote-modal-title {
              font-size: 27px;
            }

            .demirsan-quote-form-grid {
              grid-template-columns: 1fr;
            }

            .demirsan-quote-field.full-width {
              grid-column: auto;
            }

            .demirsan-quote-submit-button {
              width: 100%;
            }
          }
            /* =============================================
   TELEFON - ÜLKE KODU
============================================= */

.demirsan-phone-container {
  position: relative !important;
  width: 100% !important;
}

.demirsan-phone-container .demirsan-phone-input {
  width: 100% !important;
  height: 52px !important;

  margin: 0 !important;
  padding: 14px 16px 14px 58px !important;

  color: #17213a !important;
  background: #f6f7f9 !important;

  border: 1px solid transparent !important;
  border-radius: 9px !important;

  font-family: inherit !important;
  font-size: 14px !important;
  line-height: 1.5 !important;

  box-shadow: none !important;

  transition:
    border-color .2s ease,
    box-shadow .2s ease,
    background-color .2s ease !important;
}

.demirsan-phone-container .demirsan-phone-input:focus {
  background: #ffffff !important;

  border-color: rgba(226, 6, 20, .5) !important;

  box-shadow:
    0 0 0 4px rgba(226, 6, 20, .08) !important;
}


/* SOLDAKİ BAYRAK / ÜLKE BUTONU */

.demirsan-phone-container .demirsan-phone-button {
  width: 48px !important;

  background: transparent !important;

  border: none !important;
  border-right: 1px solid #e3e5e8 !important;

  border-radius: 9px 0 0 9px !important;
}

.demirsan-phone-container .demirsan-phone-button:hover,
.demirsan-phone-container .demirsan-phone-button.open {
  background: #eeeeef !important;
}


/* ÜLKE LİSTESİ */

.demirsan-phone-container .demirsan-phone-dropdown {
  width: 330px !important;
  max-height: 280px !important;

  margin-top: 6px !important;

  padding: 6px 0 !important;

  background: #ffffff !important;

  border: 1px solid #e1e4e8 !important;
  border-radius: 9px !important;

  box-shadow:
    0 12px 35px rgba(0, 0, 0, .14) !important;

  z-index: 1000010 !important;
}


/* ÜLKE SATIRI */

.demirsan-phone-container
.demirsan-phone-dropdown
.country {
  display: flex !important;
  align-items: center !important;

  min-height: 42px !important;

  padding:
    8px 12px 8px 46px !important;

  color: #17213a !important;

  font-size: 13px !important;
}


/* HOVER */

.demirsan-phone-container
.demirsan-phone-dropdown
.country:hover {
  background: #f6f7f9 !important;
}


/* SEÇİLİ ÜLKE */

.demirsan-phone-container
.demirsan-phone-dropdown
.country.highlight {
  background: #fff1f2 !important;
}


/* ÜLKE KODU */

.demirsan-phone-container
.demirsan-phone-dropdown
.dial-code {
  color: #8b93a5 !important;
}


/* ARAMA */

.demirsan-phone-container
.demirsan-phone-dropdown
.search {
  position: sticky !important;
  top: 0 !important;

  padding: 8px !important;

  background: #ffffff !important;

  z-index: 2 !important;
}

.demirsan-phone-container
.demirsan-phone-dropdown
.search-box {
  width: 100% !important;
  height: 40px !important;

  margin: 0 !important;
  padding: 8px 12px !important;

  background: #f6f7f9 !important;

  border: 1px solid #e1e4e8 !important;
  border-radius: 7px !important;

  font-size: 13px !important;
}
        `}
      </style>

      {/* ===============================================
          KIRMIZI TEKLİF ŞERİDİ
      ================================================ */}
      <section className="demirsan-quote-section">
        <div className="container">
          <div className="demirsan-quote-inner">
            <div className="demirsan-quote-content">
              <span className="demirsan-quote-label">Size özel çözümler</span>

              <h2 className="demirsan-quote-title">
                Projeniz için en uygun teklifi birlikte hazırlayalım
              </h2>

              <p className="demirsan-quote-description">
                Ürün, ölçü ve miktar bilgilerinizi bizimle paylaşın. Uzman
                ekibimiz size özel çözüm ve fiyatlandırma hazırlasın.
              </p>
            </div>

            <div className="demirsan-quote-action">
              <button
                type="button"
                className="demirsan-quote-button"
                onClick={openQuoteModal}
              >
                Teklif Al
                <i className="far fa-arrow-right" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===============================================
          MODAL
      ================================================ */}
      <div
        className={`demirsan-quote-modal-overlay ${
          isQuoteModalOpen ? "open" : ""
        }`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeQuoteModal();
          }
        }}
        aria-hidden={!isQuoteModalOpen}
      >
        <div
          className="demirsan-quote-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quote-modal-title"
        >
          <button
            type="button"
            className="demirsan-quote-modal-close"
            onClick={closeQuoteModal}
            aria-label="Teklif formunu kapat"
          >
            <i className="far fa-times" />
          </button>

          <span className="demirsan-quote-modal-label">Teklif formu</span>

          <h2 id="quote-modal-title" className="demirsan-quote-modal-title">
            Teklif Talebinizi Gönderin
          </h2>

          <p className="demirsan-quote-modal-description">
            İletişim bilgilerinizi ve ihtiyacınızı paylaşın. Ekibimiz sizinle
            iletişime geçsin.
          </p>

          <form onSubmit={handleQuoteSubmit}>
            <div className="demirsan-quote-form-grid">
              {/* AD SOYAD */}
              <div className="demirsan-quote-field">
                <label htmlFor="quote-full-name">Adınız Soyadınız</label>

                <input
                  id="quote-full-name"
                  type="text"
                  name="fullName"
                  value={quoteForm.fullName}
                  onChange={handleQuoteChange}
                  placeholder="Adınızı ve soyadınızı yazınız"
                  autoComplete="name"
                  inputMode="text"
                  pattern="[^0-9]*"
                  title="Ad soyad alanına rakam giremezsiniz."
                  required
                  disabled={quoteSubmitting}
                />
              </div>

              {/* TELEFON */}
              <div className="demirsan-quote-field">
                <label htmlFor="quote-phone">Telefon Numaranız</label>

                <PhoneInput
                  country="tr"
                  value={quoteForm.phone.replace(/^\+/, "")}
                  onChange={handleQuotePhoneChange}
                  enableSearch={true}
                  disableSearchIcon={true}
                  countryCodeEditable={false}
                  preferredCountries={[
                    "tr",
                    "de",
                    "gb",
                    "us",
                    "fr",
                    "nl",
                    "it",
                    "es",
                  ]}
                  searchPlaceholder="Ülke ara..."
                  searchNotFound="Ülke bulunamadı"
                  inputProps={{
                    id: "quote-phone",
                    name: "phone",
                    required: true,
                    autoComplete: "tel",
                  }}
                  disabled={quoteSubmitting}
                  containerClass="demirsan-phone-container"
                  inputClass="demirsan-phone-input"
                  buttonClass="demirsan-phone-button"
                  dropdownClass="demirsan-phone-dropdown"
                  searchClass="demirsan-phone-search"
                />
              </div>

              {/* EMAIL */}
              <div className="demirsan-quote-field full-width">
                <label htmlFor="quote-email">E-posta Adresiniz</label>

                <input
                  id="quote-email"
                  type="email"
                  name="email"
                  value={quoteForm.email}
                  onChange={handleQuoteChange}
                  placeholder="ornek@firma.com"
                  autoComplete="email"
                  required
                  disabled={quoteSubmitting}
                />
              </div>

              {/* MESAJ */}
              <div className="demirsan-quote-field full-width">
                <label htmlFor="quote-message">Mesajınız</label>

                <textarea
                  id="quote-message"
                  name="message"
                  value={quoteForm.message}
                  onChange={handleQuoteChange}
                  placeholder="Ürün, ölçü, miktar ve diğer taleplerinizi yazabilirsiniz."
                  required
                  disabled={quoteSubmitting}
                />
              </div>

              {/* ONAY KUTUSU */}
              <div className="demirsan-quote-consent">
                <input
                  id="quote-consent"
                  className="demirsan-quote-consent-checkbox"
                  type="checkbox"
                  name="consent"
                  value="true"
                  required
                  checked={isQuoteConsentChecked}
                  disabled={quoteSubmitting}
                  onChange={(event) => {
                    setIsQuoteConsentChecked(event.target.checked);
                    if (event.target.checked && quoteStatus.type === "error") {
                      setQuoteStatus({ type: "", message: "" });
                    }
                  }}
                />
                <div className="demirsan-quote-consent-content">
                  <label htmlFor="quote-consent">
                    Paylaştığım kişisel verilerin işlenmesini kabul ediyorum.{" "}
                  </label>
                  <button
                    type="button"
                    className="demirsan-quote-contract-link"
                    onClick={openQuoteContractModal}
                  >
                    Aydınlatma Metni
                  </button>
                  <span>'ni okudum.</span>
                </div>
              </div>
            </div>

            {/* BAŞARI / HATA */}
            {quoteStatus.message && (
              <div
                className={`demirsan-quote-form-status ${
                  quoteStatus.type === "error" ? "error" : "success"
                }`}
                role={quoteStatus.type === "error" ? "alert" : "status"}
                aria-live="polite"
              >
                {quoteStatus.message}
              </div>
            )}

            {/* GÖNDER */}
            <div className="demirsan-quote-submit-area">
              <button
                type="submit"
                className="demirsan-quote-submit-button"
                disabled={quoteSubmitting || !isQuoteConsentChecked}
              >
                {quoteSubmitting ? (
                  <>
                    Gönderiliyor...
                    <i className="far fa-spinner fa-spin" />
                  </>
                ) : (
                  <>
                    Teklif Talebi Gönder
                    <i className="far fa-paper-plane" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* SÖZLEŞME MODAL */}
      {quoteContractModalOpen && (
        <div
          className="demirsan-quote-contract-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeQuoteContractModal();
            }
          }}
        >
          <div
            className="demirsan-quote-contract-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-contract-modal-title"
          >
            <div className="demirsan-quote-contract-header">
              <div>
                <span className="demirsan-quote-contract-label">
                  Kişisel Verilerin Korunması
                </span>
                <h3
                  id="quote-contract-modal-title"
                  className="demirsan-quote-contract-title"
                >
                  {quoteContractData.title || "Aydınlatma Metni"}
                </h3>
              </div>
              <button
                type="button"
                className="demirsan-quote-contract-close"
                onClick={closeQuoteContractModal}
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <div className="demirsan-quote-contract-body">
              {quoteContractLoading ? (
                <div className="demirsan-quote-contract-loading">
                  Aydınlatma metni yükleniyor...
                </div>
              ) : quoteContractError ? (
                <div className="demirsan-quote-contract-error">{quoteContractError}</div>
              ) : quoteContractData.editor ? (
                <div
                  className="demirsan-quote-contract-content"
                  dangerouslySetInnerHTML={{ __html: quoteContractData.editor }}
                />
              ) : (
                <div className="demirsan-quote-contract-empty">
                  Aydınlatma metni bulunamadı.
                </div>
              )}
            </div>
            <div className="demirsan-quote-contract-footer">
              <button
                type="button"
                className="demirsan-quote-contract-footer-button"
                onClick={closeQuoteContractModal}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QuoteSection;
